import type { Action, AreaAction, ChainAction, Hook, HookTrigger, ProjectileAction, StatusApply } from '../content/actions';
import { STATUS, type Tag } from '../content/tags';
import { MOBS } from '../content/mobs';
import { TAU, dist, wrapAngle } from '../math';
import type { World } from './world';
import type { AnyUnit, MobEnt, PlayerEnt, Projectile, Zone } from './types';
import { applyStatus, stacksOf, hasStatus, removeStatus } from './status';
import { dealDamage, heal, rollCrit } from './combat';
import { spawnSummon } from './mobs';

export interface ActCtx {
  caster: AnyUnit;
  /** origin */
  x: number;
  y: number;
  /** aim / target point */
  aimX: number;
  aimY: number;
  target?: AnyUnit;
  /** base damage scalar of the caster */
  power: number;
  /** extra multiplier (skill level, weapon base damage) */
  mul: number;
  fromSkill?: string;
  /** additive count/radius/duration bonuses from skill level */
  countBonus?: number;
  radiusBonus?: number;
  durationBonus?: number;
  /** avoid recursive hook storms */
  depth?: number;
}

function isPlayer(u: AnyUnit): u is PlayerEnt {
  return u.kind === 'player';
}

function statsOf(u: AnyUnit) {
  return u.stats;
}

export function runAction(world: World, action: Action, ctx: ActCtx): void {
  const depth = ctx.depth ?? 0;
  if (depth > 4) return;
  switch (action.type) {
    case 'projectile':
      return fireProjectiles(world, action, ctx);
    case 'area':
      return doArea(world, action, ctx);
    case 'summon': {
      const c = ctx.caster;
      if (isPlayer(c) && c.flags.has('noSummons')) return;
      const count = action.count + (isPlayer(c) ? Math.floor(c.stats.summonCount) : 0) + Math.floor(ctx.countBonus ?? 0);
      const px = action.at === 'aim' ? ctx.aimX : c.x;
      const py = action.at === 'aim' ? ctx.aimY : c.y;
      const dur = action.duration + (ctx.durationBonus ?? 0);
      const scaledDur = dur * (isPlayer(c) ? c.stats.duration : 1);
      let toSpawn = count;
      if (action.unique && isPlayer(c)) {
        // a bonded companion: recasting heals and refreshes the living ones instead of stacking more
        const alive = c.summons.map((id) => world.mobs.get(id)).filter((m): m is MobEnt => !!m && !m.dead && m.def === action.unit);
        for (const m of alive) {
          heal(world, m, m.maxHp, c);
          m.until = world.tick + world.seconds(scaledDur);
        }
        toSpawn = Math.max(0, action.count - alive.length);
      }
      for (let i = 0; i < toSpawn; i++) spawnSummon(world, c, action.unit, px, py, scaledDur);
      return;
    }
    case 'buff': {
      const c = ctx.caster;
      const radius = (action.radius ?? 0) * (isPlayer(c) ? c.stats.auraRadius : 1) + (ctx.radiusBonus ?? 0);
      // a rank of a pure buff skill lengthens what it grants; nothing else here scales with level
      const bonus = ctx.durationBonus ?? 0;
      const statuses: StatusApply[] = bonus ? action.status.map((s) => ({ ...s, duration: s.duration + bonus })) : action.status;
      const cleanse = (u: AnyUnit, kind: 'buff' | 'debuff') => {
        if (!action.cleanse) return;
        for (const s of [...u.statuses]) if (STATUS[s.id]?.kind === kind && s.id !== 'invuln') removeStatus(world, u, s.id);
      };
      if (action.target === 'self') {
        cleanse(c, 'debuff');
        for (const s of statuses) applyStatus(world, c, s, c, ctx.power);
      } else if (action.target === 'allies') {
        const list = world.alliesNear(c.x, c.y, radius, c.team);
        if (!list.includes(c)) list.push(c);
        for (const u of list) {
          cleanse(u, 'debuff');
          for (const s of statuses) applyStatus(world, u, s, c, ctx.power);
        }
      } else {
        // enemies: radius <= 1 means the context target only
        if (radius <= 1) {
          if (ctx.target && !ctx.target.dead) {
            cleanse(ctx.target, 'buff');
            for (const s of statuses) applyStatus(world, ctx.target, s, c, ctx.power);
          }
        } else {
          const cx = action.at === 'self' ? c.x : ctx.aimX;
          const cy = action.at === 'self' ? c.y : ctx.aimY;
          for (const u of world.enemiesNear(cx, cy, radius, c.team)) {
            cleanse(u, 'buff');
            for (const s of statuses) applyStatus(world, u, s, c, ctx.power);
          }
        }
      }
      if (action.visual) world.events.push({ t: 'fx', kind: action.visual, x: c.x, y: c.y, r: radius, team: c.team });
      return;
    }
    case 'stance': {
      const c = ctx.caster;
      const list = action.statuses;
      let idx = -1;
      for (let i = 0; i < list.length; i++) {
        if (hasStatus(c, list[i])) {
          idx = i;
          removeStatus(world, c, list[i]);
        }
      }
      const next = list[(idx + 1) % list.length];
      applyStatus(world, c, { id: next, duration: (action.duration + (ctx.durationBonus ?? 0)) * (isPlayer(c) ? c.stats.duration : 1) }, c, ctx.power);
      if (action.visual) world.events.push({ t: 'fx', kind: action.visual, x: c.x, y: c.y, r: 60, team: c.team });
      return;
    }
    case 'heal': {
      const c = ctx.caster;
      const radius = (action.radius ?? 0) * (isPlayer(c) ? c.stats.auraRadius : 1);
      const amountFor = (u: AnyUnit) => (action.pct ? u.maxHp * action.pct : 0) + (action.power ? ctx.power * action.power * ctx.mul : 0);
      if (action.target === 'self') heal(world, c, amountFor(c), c);
      else if (action.target === 'allies') {
        const list = world.alliesNear(c.x, c.y, radius, c.team);
        if (!list.includes(c)) list.push(c);
        for (const u of list) heal(world, u, amountFor(u), c);
      } else {
        const list = world.alliesNear(c.x, c.y, radius, c.team).filter((u) => u.kind === 'player' && u.hp < u.maxHp);
        list.sort((a, b) => a.hp / a.maxHp - b.hp / b.maxHp);
        if (list.length) heal(world, list[0], amountFor(list[0]), c);
        else if (c.hp < c.maxHp) heal(world, c, amountFor(c), c);
      }
      if (isPlayer(c) && c.flags.has('holySmite') && action.target !== 'self') {
        for (const e of world.enemiesNear(c.x, c.y, Math.max(radius, 120), c.team).slice(0, 6)) dealDamage(world, c, e, ctx.power * 0.6 * ctx.mul, ['holy'], { fromSkill: ctx.fromSkill });
      }
      if (action.visual) world.events.push({ t: 'fx', kind: action.visual, x: c.x, y: c.y, r: radius, team: c.team });
      return;
    }
    case 'dash': {
      const c = ctx.caster;
      const a = Math.atan2(ctx.aimY - c.y, ctx.aimX - c.x);
      const sx = c.x;
      const sy = c.y;
      const steps = 6;
      const hit = new Set<number>();
      const radius = (action.radius ?? 40) * (isPlayer(c) ? c.stats.area : 1);
      for (let i = 1; i <= steps; i++) {
        const nx = sx + Math.cos(a) * (action.distance * i) / steps;
        const ny = sy + Math.sin(a) * (action.distance * i) / steps;
        const p = world.map.resolveCircle(nx, ny, c.r);
        if (Math.abs(p.x - nx) > 1 || Math.abs(p.y - ny) > 1) break;
        c.x = p.x;
        c.y = p.y;
        if (action.damage) {
          for (const e of world.enemiesNear(c.x, c.y, radius, c.team)) {
            if (hit.has(e.id)) continue;
            hit.add(e.id);
            const { crit, mult } = rollCrit(world, c);
            dealDamage(world, c, e, ctx.power * action.damage * ctx.mul * statsOf(c).damage * mult, action.tags ?? ['blunt'], { crit, fromSkill: ctx.fromSkill });
            if (action.status) for (const s of action.status) applyStatus(world, e, s, c, ctx.power);
          }
        }
      }
      c.facing = a;
      world.events.push({ t: 'fx', kind: action.visual ?? 'dash', x: sx, y: sy, x2: c.x, y2: c.y, a, team: c.team });
      if (action.trail) runAction(world, action.trail, { ...ctx, x: c.x, y: c.y, aimX: c.x, aimY: c.y, depth: depth + 1 });
      return;
    }
    case 'chain':
      return doChain(world, action, ctx);
    case 'cost': {
      const c = ctx.caster;
      const loss = Math.max(0, Math.round(c.hp * action.hpPct));
      if (c.hp - loss < 1) return;
      c.hp -= loss;
      world.events.push({ t: 'hit', id: c.id, x: c.x, y: c.y, dmg: loss, crit: false, tag: 'bleed', team: -1 });
      return;
    }
    case 'multi':
      for (const a of action.actions) runAction(world, a, { ...ctx, depth: depth + 1 });
      return;
  }
}

function fireProjectiles(world: World, a: ProjectileAction, ctx: ActCtx): void {
  const c = ctx.caster;
  const st = statsOf(c);
  const player = isPlayer(c);
  let count = a.count + Math.floor(ctx.countBonus ?? 0);
  if (player && a.tags.includes('projectile') && !a.orbit) count += Math.floor(st.projCount);
  const speed = a.speed * (player ? st.projSpeed : 1);
  const range = a.range * (player ? st.range : 1);
  const dmg = ctx.power * a.damage * ctx.mul * st.damage;
  let baseAngle: number;
  if (a.pattern === 'nearest') {
    const e = world.nearestEnemy(c.x, c.y, range, c.team);
    baseAngle = e ? Math.atan2(e.y - c.y, e.x - c.x) : c.facing;
  } else if (a.pattern === 'forward') {
    baseAngle = c.facing;
  } else if (a.pattern === 'random') {
    baseAngle = world.rng.angle();
  } else {
    baseAngle = Math.atan2(ctx.aimY - c.y, ctx.aimX - c.x);
    if (!Number.isFinite(baseAngle)) baseAngle = c.facing;
  }
  const until = world.tick + Math.max(1, Math.round((range / Math.max(1, speed)) * 20));
  for (let i = 0; i < count; i++) {
    let ang: number;
    if (a.pattern === 'ring' || a.orbit) ang = baseAngle + (i / count) * TAU;
    else if (a.pattern === 'spiral') ang = baseAngle + (i / count) * TAU + world.tick * 0.15;
    else if (a.pattern === 'random') ang = world.rng.angle();
    else ang = count === 1 ? baseAngle + (a.spread ? world.rng.float(-a.spread / 2, a.spread / 2) * 0.3 : 0) : baseAngle - a.spread / 2 + (a.spread * i) / (count - 1);
    const id = world.allocId();
    const pr: Projectile = {
      id,
      x: c.x,
      y: c.y,
      vx: Math.cos(ang) * speed,
      vy: Math.sin(ang) * speed,
      r: a.radius,
      team: c.team,
      owner: c.id,
      damage: dmg,
      tags: a.tags,
      pierce: (a.pierce ?? 0) + (player && a.tags.includes('projectile') ? Math.floor(st.pierce) : 0),
      bounce: a.bounce ?? 0,
      until: a.orbit ? world.tick + Math.round((a.orbit.duration + (ctx.durationBonus ?? 0)) * 20) : until,
      hits: new Set(),
      visual: a.visual,
      status: a.status,
      onHit: a.onHit,
      onExpire: a.onExpire,
      homing: a.homing ?? 0,
      targetId: 0,
      knockback: a.knockback ?? 0,
      orbit: a.orbit ? { radius: a.orbit.radius * (player ? st.area : 1), angle: ang, angVel: a.orbit.angVel } : undefined,
      returning: !!a.returning,
      returned: false,
      spawnTick: world.tick,
      power: ctx.power,
      critChance: st.critChance,
      critMult: st.critMult,
      fromSkill: ctx.fromSkill,
    };
    if (pr.orbit) {
      pr.x = c.x + Math.cos(ang) * pr.orbit.radius;
      pr.y = c.y + Math.sin(ang) * pr.orbit.radius;
      pr.vx = 0;
      pr.vy = 0;
    }
    world.projectiles.set(id, pr);
    world.events.push({ t: 'proj', id, x: pr.x, y: pr.y, vx: pr.vx, vy: pr.vy, r: pr.r, visual: pr.visual, team: pr.team, until: pr.until, owner: c.id, orbit: pr.orbit ? { radius: pr.orbit.radius, angVel: pr.orbit.angVel } : undefined });
  }
}

function doArea(world: World, a: AreaAction, ctx: ActCtx): void {
  const c = ctx.caster;
  const st = statsOf(c);
  const player = isPlayer(c);
  const areaMul = player ? st.area : 1;
  let cx = c.x;
  let cy = c.y;
  if (a.at === 'aim') {
    cx = ctx.aimX;
    cy = ctx.aimY;
  } else if (a.at === 'target') {
    if (ctx.target) {
      cx = ctx.target.x;
      cy = ctx.target.y;
    } else {
      cx = ctx.aimX;
      cy = ctx.aimY;
    }
  } else if (a.at === 'nearest') {
    const e = world.nearestEnemy(c.x, c.y, 500 * (player ? st.range : 1), c.team);
    if (e) {
      cx = e.x;
      cy = e.y;
    }
  }
  const radius = (a.radius + (ctx.radiusBonus ?? 0)) * areaMul;
  const dmgBase = ctx.power * a.damage * ctx.mul * st.damage;
  if (a.at === 'melee') {
    const reach = (a.range ?? 90) * (player ? st.range : 1);
    const arc = (a.arc ?? 2) * Math.sqrt(areaMul);
    const aim = Math.atan2(ctx.aimY - c.y, ctx.aimX - c.x);
    c.facing = Number.isFinite(aim) ? aim : c.facing;
    for (const e of world.enemiesNear(c.x, c.y, reach + e_r_max, c.team)) {
      const d = dist(c.x, c.y, e.x, e.y) - e.r;
      if (d > reach) continue;
      const da = Math.abs(wrapAngle(Math.atan2(e.y - c.y, e.x - c.x) - c.facing));
      if (da > arc / 2 && d > 20) continue;
      hitUnit(world, c, e, dmgBase, a, ctx, cx, cy);
    }
    world.events.push({ t: 'fx', kind: a.visual, x: c.x, y: c.y, r: reach, a: c.facing, team: c.team, id: c.id });
    return;
  }
  if (a.duration && a.duration > 0) {
    const dur = a.duration + (ctx.durationBonus ?? 0);
    const tickRate = a.tickRate ?? 2;
    const id = world.allocId();
    const z: Zone = {
      id,
      x: cx,
      y: cy,
      r: radius,
      team: c.team,
      owner: c.id,
      damage: dmgBase / tickRate,
      tags: a.tags,
      status: a.status,
      until: world.tick + Math.round(dur * (player ? st.duration : 1) * 20),
      tickEvery: Math.max(1, Math.round(20 / tickRate)),
      nextTick: world.tick,
      visual: a.visual,
      pull: a.pull ?? 0,
      healAllies: (a.healAllies ?? 0) * ctx.power * ctx.mul,
      power: ctx.power,
      knockback: a.knockback ?? 0,
    };
    world.zones.set(id, z);
    return;
  }
  const enemies = world.enemiesNear(cx, cy, radius, c.team);
  for (const e of enemies) hitUnit(world, c, e, dmgBase, a, ctx, cx, cy);
  if (a.healAllies) {
    for (const u of world.alliesNear(cx, cy, radius, c.team)) heal(world, u, ctx.power * a.healAllies * ctx.mul, c);
  }
  world.events.push({ t: 'fx', kind: a.visual, x: cx, y: cy, r: radius, team: c.team });
}

const e_r_max = 40;

function hitUnit(world: World, c: AnyUnit, e: AnyUnit, dmgBase: number, a: AreaAction, ctx: ActCtx, cx: number, cy: number): void {
  let dmg = dmgBase;
  if (a.consume) {
    const stacks = stacksOf(e, a.consume.status);
    if (stacks > 0) {
      dmg *= 1 + stacks * a.consume.perStack;
      e.statuses = e.statuses.filter((s) => s.id !== a.consume!.status);
    }
  }
  const { crit, mult } = rollCrit(world, c);
  if (dmg > 0) dealDamage(world, c, e, dmg * mult, a.tags, { crit, fromSkill: ctx.fromSkill });
  if (e.dead) return;
  if (a.status) for (const s of a.status) applyStatus(world, e, s, c, ctx.power);
  if (a.knockback) {
    const ang = Math.atan2(e.y - cy, e.x - cx);
    e.knockX += Math.cos(ang) * a.knockback;
    e.knockY += Math.sin(ang) * a.knockback;
  }
  if (a.pull) {
    const ang = Math.atan2(cy - e.y, cx - e.x);
    e.knockX += Math.cos(ang) * a.pull * 0.5;
    e.knockY += Math.sin(ang) * a.pull * 0.5;
  }
}

function doChain(world: World, a: ChainAction, ctx: ActCtx): void {
  const c = ctx.caster;
  const st = statsOf(c);
  const player = isPlayer(c);
  const range = a.range * (player ? st.range : 1);
  let cur: AnyUnit | undefined = ctx.target && !ctx.target.dead ? ctx.target : world.nearestEnemy(ctx.aimX, ctx.aimY, 160, c.team) ?? world.nearestEnemy(c.x, c.y, range * 2, c.team);
  if (!cur) return;
  const hit = new Set<number>();
  let px = c.x;
  let py = c.y;
  const jumps = a.jumps + Math.floor(ctx.countBonus ?? 0);
  for (let i = 0; i < jumps && cur; i++) {
    hit.add(cur.id);
    world.events.push({ t: 'fx', kind: a.visual, x: px, y: py, x2: cur.x, y2: cur.y, team: c.team });
    const { crit, mult } = rollCrit(world, c);
    dealDamage(world, c, cur, ctx.power * a.damage * ctx.mul * st.damage * mult, a.tags, { crit, fromSkill: ctx.fromSkill });
    if (a.status && !cur.dead) for (const s of a.status) applyStatus(world, cur, s, c, ctx.power);
    px = cur.x;
    py = cur.y;
    const nextCandidates = world.enemiesNear(px, py, range, c.team).filter((u) => !hit.has(u.id));
    cur = nextCandidates[0];
  }
}

export interface HookCtx {
  target?: AnyUnit;
  tags?: Tag[];
  x: number;
  y: number;
  /** skill id for 'cast' triggers */
  skill?: string;
}

/** Runs hooks of a trigger for a player (traits, keystones, aspects, items, hero trait). */
export function fireHooks(world: World, p: PlayerEnt, trigger: HookTrigger, hctx: HookCtx, depth = 0): void {
  if (depth > 2 || p.dead) return;
  for (const src of p.perks) {
    if (trigger === 'cast' && src.skill && src.skill !== hctx.skill) continue;
    for (let i = 0; i < src.hooks.length; i++) {
      const h: Hook = src.hooks[i];
      if (h.on !== trigger) continue;
      if (h.tags && (!hctx.tags || !h.tags.some((t) => hctx.tags!.includes(t)))) continue;
      if (h.hpBelow !== undefined && p.hp / p.maxHp > h.hpBelow) continue;
      const key = src.key + ':' + i;
      if (h.cooldown) {
        const ready = p.hookCds.get(key) ?? 0;
        if (world.tick < ready) continue;
      }
      if (h.chance !== undefined && !world.rng.chance(h.chance)) continue;
      if (h.cooldown) p.hookCds.set(key, world.tick + Math.round(h.cooldown * 20));
      const tx = hctx.target ? hctx.target.x : hctx.x;
      const ty = hctx.target ? hctx.target.y : hctx.y;
      runAction(world, h.action, {
        caster: p,
        x: p.x,
        y: p.y,
        aimX: tx,
        aimY: ty,
        target: hctx.target,
        power: p.power,
        mul: 1,
        fromSkill: src.key,
        depth: depth + 1,
      });
    }
  }
}

/** Periodic hooks are evaluated once per second. */
export function firePeriodicHooks(world: World, p: PlayerEnt): void {
  for (const src of p.perks) {
    for (let i = 0; i < src.hooks.length; i++) {
      const h = src.hooks[i];
      if (h.on !== 'periodic') continue;
      const key = src.key + ':' + i;
      const ready = p.hookCds.get(key) ?? 0;
      if (world.tick < ready) continue;
      p.hookCds.set(key, world.tick + Math.round((h.period ?? 5) * 20));
      runAction(world, h.action, { caster: p, x: p.x, y: p.y, aimX: p.x + Math.cos(p.facing) * 100, aimY: p.y + Math.sin(p.facing) * 100, power: p.power, mul: 1, fromSkill: src.key, depth: 1 });
    }
  }
}

export function mobDefName(def: string): string {
  return MOBS[def]?.id ?? def;
}

export type { StatusApply };
